/**
 * TimeSlotsGrid Component
 *
 * Main grid component that renders all timeslots grouped by time period.
 * Groups timeslots into Morning, Afternoon, and Evening sections with
 * visual separators between them.
 *
 * Requirements: 1.1, 1.2, 1.8
 * - 1.1: Display timeslots in a 7-column responsive grid layout
 * - 1.2: Group timeslots into three Time_Periods (Morning, Afternoon, Evening)
 * - 1.8: Clearly distinguish between the three Slot_Status states visually
 */

import React, { useMemo } from 'react'
import styled from 'styled-components'
import { TimeSlot, TIME_PERIODS } from '../types'
import { groupSlotsByPeriod } from '../utils/slotUtils'
import { COLORS } from '../utils/theme'
import TimePeriodSection from './TimePeriodSection'

/**
 * Props for the TimeSlotsGrid component
 */
export interface TimeSlotsGridProps {
  /** Array of all timeslots to display */
  timeSlots: TimeSlot[]
  /** Set of currently selected slot IDs */
  selectedSlotIds: Set<string>
  /** Set of slot IDs highlighted during drag selection */
  highlightedSlotIds: Set<string>
  /** Click handler for individual slots */
  onSlotClick: (slot: TimeSlot, event: React.MouseEvent) => void
}

/**
 * Container for the entire timeslots grid
 */
const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;
`

/**
 * Visual separator between time period sections
 * Requirement 1.8: Clear visual distinction between sections
 */
const PeriodSeparator = styled.div`
  height: 1px;
  background-color: ${COLORS.border};
  margin: 0 16px;
`

/**
 * TimeSlotsGrid Component
 *
 * Renders the complete timeslot grid with Morning, Afternoon, and Evening sections.
 * Each section displays its timeslots in a 7-column grid layout.
 *
 * @param props - Component props
 * @returns React element representing the complete timeslots grid
 */
const TimeSlotsGrid: React.FC<TimeSlotsGridProps> = ({
  timeSlots,
  selectedSlotIds,
  highlightedSlotIds,
  onSlotClick,
}) => {
  // Group timeslots by time period using memoization for performance
  const slotsByPeriod = useMemo(
    () => groupSlotsByPeriod(timeSlots),
    [timeSlots]
  )

  return (
    <GridContainer>
      {TIME_PERIODS.map((period, index) => {
        const periodSlots = slotsByPeriod.get(period.id) || []

        // Only render sections that have slots
        if (periodSlots.length === 0) {
          return null
        }

        return (
          <React.Fragment key={period.id}>
            {/* Add separator before sections (except the first one) */}
            {index > 0 && <PeriodSeparator />}
            <TimePeriodSection
              title={period.titleKey}
              timeRange={period.timeRangeKey}
              slots={periodSlots}
              selectedSlotIds={selectedSlotIds}
              highlightedSlotIds={highlightedSlotIds}
              onSlotClick={onSlotClick}
            />
          </React.Fragment>
        )
      })}
    </GridContainer>
  )
}

export default React.memo(TimeSlotsGrid)
